import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { adminTeamsAPI } from '../services/adminApi';

const MemberCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const MemberHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MemberAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.125rem;
  flex-shrink: 0;
`;

const MemberBasicInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const MemberPosition = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: capitalize;
`;

const MemberDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MemberDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }

  .label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
    flex-shrink: 0;
  }

  .value {
    font-size: 0.875rem;
    color: #1f2937;
    font-weight: 500;
    text-align: right;
    max-width: 60%;
    word-break: break-word;
  }
`;

const SkillLevelBadge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) => {
    const level = Number(p.$level);
    if (level >= 8) return '#fef3c7';
    if (level >= 5) return '#fed7aa';
    return '#dbeafe';
  }};
  color: ${(p) => {
    const level = Number(p.$level);
    if (level >= 8) return '#92400e';
    if (level >= 5) return '#9a3412';
    return '#043663';
  }};
`;

const PaymentBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  background: ${(p) => p.$tone.bg};
  color: ${(p) => p.$tone.fg};
`;

const PaymentToggle = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.45rem;
`;

const PaymentToggleBtn = styled.button`
  appearance: none;
  border: 1px solid ${(p) => (p.$active ? p.$color : '#d1d5db')};
  background: ${(p) => (p.$active ? p.$color : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#374151')};
  border-radius: 999px;
  padding: 0.2rem 0.65rem;
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) {
    filter: brightness(0.96);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PAYMENT_TONES = {
  paid: { bg: '#dcfce7', fg: '#166534', label: 'Paid' },
  team_paid: { bg: '#dcfce7', fg: '#166534', label: 'Paid (team)' },
  pending: { bg: '#fef3c7', fg: '#92400e', label: 'Pending' },
  cancelled: { bg: '#fee2e2', fg: '#991b1b', label: 'Cancelled' },
  expired: { bg: '#fee2e2', fg: '#991b1b', label: 'Expired' },
  none: { bg: '#f3f4f6', fg: '#4b5563', label: 'No invoice' },
};

/** Phase payment state for this member: own invoice first, then legacy team-level payment. */
function paymentState(member) {
  const invoice = member.registration_invoice_status;
  const teamPaid = member.team_payment_status === 'paid';

  // A cancelled/expired invoice on a team the league was paid for is a dead
  // checkout attempt, not an unpaid member. Team payment wins.
  if (teamPaid && (invoice === 'cancelled' || invoice === 'expired' || !invoice)) {
    return { key: 'team_paid', tone: PAYMENT_TONES.team_paid };
  }

  if (invoice && PAYMENT_TONES[invoice]) return { key: invoice, tone: PAYMENT_TONES[invoice] };
  if (invoice) return { key: invoice, tone: { ...PAYMENT_TONES.none, label: invoice } };
  if (teamPaid) return { key: 'team_paid', tone: PAYMENT_TONES.team_paid };
  if (member.team_payment_status === undefined || member.team_payment_status === null) return null;
  return { key: 'none', tone: PAYMENT_TONES.none };
}

function fmt(value, fallback = 'Not provided') {
  if (value == null || value === '') return fallback;
  return String(value);
}

function fmtDate(value) {
  if (!value) return 'Not provided';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'Not provided' : d.toLocaleDateString();
}

function fmtSex(value) {
  if (!value) return 'Not provided';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1).replace('_', ' ');
}

function fmtBool(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Not provided';
}

function fmtKitSize(member) {
  const shirt = member.t_shirt_size || member.tShirtSize;
  const short = member.short_size || member.shortSize;
  if (shirt && short && shirt !== short) return `Shirt ${shirt}, Short ${short}`;
  return shirt || short || null;
}

function fmtNationality(member) {
  const primary = member.nationality_primary || member.nationalityPrimary;
  const secondary = member.nationality_secondary || member.nationalitySecondary;
  const parts = [primary, secondary].filter(Boolean);
  return parts.length ? parts.join(' / ') : null;
}

function fmtAddress(member) {
  const street = member.street_address || member.streetAddress;
  const postal = member.postal_code || member.postalCode;
  const city = member.city;
  const country = member.country;
  const line2 = [postal, city].filter(Boolean).join(' ');
  const parts = [street, line2, country].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function fmtPosition(member) {
  const pos = member.position || 'member';
  return pos.charAt(0).toUpperCase() + pos.slice(1);
}

function DetailRow({ label, value, fallback = 'Not provided', children }) {
  return (
    <MemberDetailItem>
      <span className="label">{label}</span>
      <span className="value">{children ?? fmt(value, fallback)}</span>
    </MemberDetailItem>
  );
}

function isPaidKey(key) {
  return key === 'paid' || key === 'team_paid';
}

export default function AdminTeamMemberProfileCard({
  member,
  teamId,
  seasonId,
  onPaymentUpdated,
}) {
  const { adminUser } = useAdminAuth();
  const canEdit = !!(adminUser?.isSuperAdmin || adminUser?.permissions?.teams?.edit);
  const [saving, setSaving] = useState(false);
  const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
  const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim();
  const instagram = member.instagram_handle || member.instagramHandle;
  const homeCanton = member.home_canton || member.homeCanton;
  const playerId = member.player_id || member.playerId;
  const payment = paymentState(member);
  const amount = member.registration_invoice_total_chf;
  const showPaymentToggle = canEdit && teamId && seasonId && playerId;
  const currentPaid = payment ? isPaidKey(payment.key) : false;

  const changePayment = async (nextStatus) => {
    if (!showPaymentToggle || saving) return;
    if (nextStatus === 'paid' && currentPaid) return;
    if (nextStatus === 'pending' && payment?.key === 'pending') return;
    const label = nextStatus === 'paid' ? 'Paid' : 'Pending';
    const ok = window.confirm(
      `Set ${fullName || 'this player'} to ${label} for the selected phase?\n\nThis only updates the app. It does not charge or refund Stripe.`
    );
    if (!ok) return;
    setSaving(true);
    try {
      await adminTeamsAPI.updateMemberRegistrationPayment(teamId, playerId, {
        status: nextStatus,
        seasonId,
      });
      toast.success(`Payment set to ${label}`);
      if (typeof onPaymentUpdated === 'function') await onPaymentUpdated();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not update payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <MemberCard>
      <MemberHeader>
        <MemberAvatar>{initials || '?'}</MemberAvatar>
        <MemberBasicInfo>
          <MemberName>
            {fullName || 'Unknown Player'}
            {payment ? (
              <>
                {' '}
                <PaymentBadge
                  $tone={payment.tone}
                  title={
                    amount != null
                      ? `Registration invoice for this phase: ${payment.tone.label} (CHF ${amount})`
                      : 'Registration payment for this phase'
                  }
                >
                  {payment.tone.label}
                </PaymentBadge>
              </>
            ) : null}
          </MemberName>
          <MemberPosition>
            {member.position === 'captain' ? '👑' : '👤'} {fmtPosition(member)}
          </MemberPosition>
          {showPaymentToggle ? (
            <PaymentToggle>
              <PaymentToggleBtn
                type="button"
                $active={currentPaid}
                $color="#166534"
                disabled={saving || currentPaid}
                onClick={() => changePayment('paid')}
              >
                Paid
              </PaymentToggleBtn>
              <PaymentToggleBtn
                type="button"
                $active={payment?.key === 'pending'}
                $color="#92400e"
                disabled={saving || payment?.key === 'pending'}
                onClick={() => changePayment('pending')}
              >
                Pending
              </PaymentToggleBtn>
            </PaymentToggle>
          ) : null}
        </MemberBasicInfo>
      </MemberHeader>

      <MemberDetails>
        <DetailRow label="Email" value={member.email} />
        <DetailRow label="Phone" value={member.phone_number || member.phoneNumber} />
        <DetailRow label="Skill level">
          <SkillLevelBadge $level={member.skill_level}>
            {member.skill_level ?? 'N/A'}
          </SkillLevelBadge>
        </DetailRow>
        <DetailRow label="Home club" value={member.home_club || member.homeClub} fallback="Not specified" />
        <DetailRow label="Home canton" value={homeCanton} />
        <DetailRow label="Kit size" value={fmtKitSize(member)} />
        <DetailRow label="Nationality" value={fmtNationality(member)} />
        <DetailRow label="Sex" value={fmtSex(member.sex)} />
        <DetailRow label="Date of birth" value={fmtDate(member.date_of_birth || member.dateOfBirth)} />
        <DetailRow label="Address" value={fmtAddress(member)} />
        <DetailRow label="Dominant hand" value={member.dominant_hand || member.dominantHand} />
        <DetailRow
          label="Preferred court side"
          value={member.preferred_court_side || member.preferredCourtSide}
        />
        <DetailRow
          label="Swiss Tennis Padel licensed"
          value={fmtBool(
            member.swiss_tennis_padel_licensed ?? member.swissTennisPadelLicensed
          )}
        />
        <DetailRow label="UI language" value={member.ui_locale || member.uiLocale} />
        <DetailRow
          label="Profile public"
          value={fmtBool(member.profile_public ?? member.profilePublic)}
        />
        <DetailRow label="Instagram">
          {instagram ? (
            <a
              href={`https://instagram.com/${String(instagram).replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#043663', textDecoration: 'none' }}
            >
              @{String(instagram).replace('@', '')}
            </a>
          ) : (
            'Not provided'
          )}
        </DetailRow>
        {playerId ? (
          <DetailRow label="Player ID">
            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{playerId}</span>
          </DetailRow>
        ) : null}
        <DetailRow label="Member ID">
          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {member.id?.substring(0, 8)}…
          </span>
        </DetailRow>
        <DetailRow
          label="Joined team"
          value={fmtDate(member.created_at || member.joinedAt)}
          fallback="Unknown"
        />
      </MemberDetails>
    </MemberCard>
  );
}

export const AdminTeamMembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
`;
